/**
 * Quick Add Drawer - Theme Matched Design
 * Shows all variants with images and quantity selectors
 * OPTIMIZED: Product caching, single HTML fetch, event delegation
 * PERF: Section Rendering API, Viewport prefetch, CSS preload
 * Updated: 2026-01-15
 */

if (!customElements.get('quick-add-drawer-fun')) {
  // Shared product cache across all instances (persists for session)
  const productCache = new Map();
  const pendingFetches = new Map();
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL
  const viewportPrefetched = new Set(); // Track viewport-prefetched URLs

  class QuickAddDrawerFun extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.productUrl = null;
      this.variants = [];
      this.variantQuantities = {};
      this.addedVariants = new Set();

      this.addedQuantities = {}; // Track total quantity added per variant

      this.handleQuickAddClick = this.handleQuickAddClick.bind(this);
      this.handleQuickAddHover = this.handleQuickAddHover.bind(this);
      this.handleContentClick = this.handleContentClick.bind(this);
      this.close = this.close.bind(this);
    }

    /**
     * Convert product URL to use Section Rendering API for faster loading
     * @param {string} productUrl - The original product URL
     * @returns {string} - URL with section_id parameter
     */
    getSectionRenderingUrl(productUrl) {
      const separator = productUrl.includes('?') ? '&' : '?';
      return `${productUrl}${separator}section_id=quick-add-product`;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      if (this.observer) this.observer.disconnect();
    }

    init() {
      this.backdrop = this.querySelector('.quick-add-drawer-fun__backdrop');
      this.container = this.querySelector('.quick-add-drawer-fun__container');
      this.closeBtn = this.querySelector('.quick-add-drawer-fun__close');
      this.content = this.querySelector('.quick-add-drawer-fun__content');
      this.footer = this.querySelector('.quick-add-drawer-fun__footer');

      this.addEventListeners();
      this.initSwipeGesture();
      this.observeNewButtons();
    }

    addEventListeners() {
      // Use capture for faster click handling
      document.addEventListener('click', this.handleQuickAddClick, true);

      // Hover prefetch for desktop
      if (window.matchMedia('(hover: hover)').matches) {
        this.bindHoverListeners();
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.close);
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', this.close);
      }

      // Use event delegation for content interactions
      this.content.addEventListener('click', this.handleContentClick);

      // Listen for quantity input changes
      this.content.addEventListener('input', (e) => {
        if (e.target.classList.contains('qa-variant__qty-input')) {
          this.handleQuantityInput(e.target);
        }
      });

      // Select all text on focus for easy editing
      this.content.addEventListener('focus', (e) => {
        if (e.target.classList.contains('qa-variant__qty-input')) {
          e.target.select();
        }
      }, true);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    bindHoverListeners() {
      document.querySelectorAll('.js-quick-add').forEach(btn => {
        if (!btn.dataset.hoverBound) {
          btn.addEventListener('mouseenter', this.handleQuickAddHover);
          btn.dataset.hoverBound = 'true';
        }
      });
    }

    // Watch for dynamically added quick-add buttons
    observeNewButtons() {
      if (!window.matchMedia('(hover: hover)').matches) return;

      this.observer = new MutationObserver((mutations) => {
        let hasNewButtons = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains('js-quick-add') || node.querySelector?.('.js-quick-add')) {
                hasNewButtons = true;
              }
            }
          });
        });
        if (hasNewButtons) {
          this.bindHoverListeners();
          this.setupViewportPrefetch(); // Also setup viewport prefetch for new buttons
        }
      });

      this.observer.observe(document.body, { childList: true, subtree: true });

      // Initial viewport prefetch setup
      this.setupViewportPrefetch();
    }

    /**
     * Setup Intersection Observer to prefetch products as they scroll into view
     * This loads data before user even hovers, for near-instant open
     */
    setupViewportPrefetch() {
      if (this.viewportObserver) return; // Already setup

      // Use requestIdleCallback to not block initial page load
      const setup = () => {
        this.viewportObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const btn = entry.target;
              const productUrl = btn.dataset.productUrl;

              if (productUrl && !viewportPrefetched.has(productUrl)) {
                viewportPrefetched.add(productUrl);
                // Low priority prefetch - use requestIdleCallback
                if ('requestIdleCallback' in window) {
                  requestIdleCallback(() => this.prefetchProduct(this.normalizeUrl(productUrl)), { timeout: 2000 });
                } else {
                  setTimeout(() => this.prefetchProduct(this.normalizeUrl(productUrl)), 100);
                }
              }
            }
          });
        }, {
          rootMargin: '200px', // Start prefetching 200px before entering viewport
          threshold: 0
        });

        // Observe all quick-add buttons
        document.querySelectorAll('.js-quick-add').forEach(btn => {
          if (btn.dataset.productUrl) {
            this.viewportObserver.observe(btn);
          }
        });
      };

      // Delay setup to not impact initial page load
      if ('requestIdleCallback' in window) {
        requestIdleCallback(setup, { timeout: 3000 });
      } else {
        setTimeout(setup, 1000);
      }
    }

    normalizeUrl(productUrl) {
      try {
        const url = new URL(productUrl, window.location.origin);
        return url.pathname;
      } catch (e) {
        return productUrl;
      }
    }

    removeEventListeners() {
      document.removeEventListener('click', this.handleQuickAddClick, true);
    }

    initSwipeGesture() {
      let startY = 0;
      let currentY = 0;

      const handle = this.querySelector('.quick-add-drawer-fun__handle');
      if (!handle) return;

      handle.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
      }, { passive: true });

      handle.addEventListener('touchmove', (e) => {
        currentY = e.touches[0].clientY;
        const diff = currentY - startY;
        if (diff > 0) {
          this.container.style.transform = `translateY(${diff}px)`;
        }
      }, { passive: true });

      handle.addEventListener('touchend', () => {
        const diff = currentY - startY;
        this.container.style.transform = '';
        if (diff > 100) {
          this.close();
        }
        startY = 0;
        currentY = 0;
      }, { passive: true });
    }

    handleQuickAddClick(e) {
      const btn = e.target.closest('.js-quick-add');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      this.open(btn);
    }

    // Event delegation handler for all content clicks
    handleContentClick(e) {
      const target = e.target;

      // Quantity buttons
      const qtyBtn = target.closest('.qa-variant__qty-btn');
      if (qtyBtn) {
        this.handleQuantityChange(qtyBtn);
        return;
      }

      // Add to cart buttons
      const addBtn = target.closest('.qa-variant__add');
      if (addBtn && !addBtn.classList.contains('is-loading')) {
        this.selectVariant(addBtn.dataset.variantId);
        this.addVariantToCart(addBtn);
        return;
      }

      // Variant card selection
      const variantCard = target.closest('.qa-variant');
      if (variantCard && !target.closest('.qa-variant__controls')) {
        this.selectVariant(variantCard.dataset.variantId);
      }
    }

    // Handle direct quantity input changes
    handleQuantityInput(input) {
      const variantId = input.dataset.variantId;
      let qty = parseInt(input.value, 10);

      // Validate the input
      if (isNaN(qty) || qty < 0) {
        qty = 0;
      }
      // Cap at reasonable maximum
      if (qty > 999) {
        qty = 999;
      }

      input.value = qty;
      this.variantQuantities[variantId] = qty;

      // Update decrease button state
      const decreaseBtn = this.content.querySelector(`.qa-variant__qty-btn[data-action="decrease"][data-variant-id="${variantId}"]`);
      const qtyContainer = this.content.querySelector(`.qa-variant__qty[data-variant-id="${variantId}"]`);

      if (decreaseBtn) decreaseBtn.disabled = qty <= 0;

      // Toggle is-active class based on quantity
      if (qtyContainer) {
        if (qty > 0) {
          qtyContainer.classList.add('is-active');
        } else {
          qtyContainer.classList.remove('is-active');
        }
      }

      this.selectVariant(variantId);
    }

    handleQuantityChange(btn) {
      const variantId = btn.dataset.variantId;
      const action = btn.dataset.action;
      const inputEl = this.content.querySelector(`.qa-variant__qty-input[data-variant-id="${variantId}"]`);
      const decreaseBtn = this.content.querySelector(`.qa-variant__qty-btn[data-action="decrease"][data-variant-id="${variantId}"]`);
      const qtyContainer = this.content.querySelector(`.qa-variant__qty[data-variant-id="${variantId}"]`);

      // Default to 0, not 1
      let qty = this.variantQuantities[variantId] || 0;

      if (action === 'increase') {
        qty++;
      } else if (action === 'decrease' && qty > 0) {
        qty--;
      }

      this.variantQuantities[variantId] = qty;
      if (inputEl) inputEl.value = qty;
      if (decreaseBtn) decreaseBtn.disabled = qty <= 0;

      // Toggle is-active class based on quantity
      if (qtyContainer) {
        if (qty > 0) {
          qtyContainer.classList.add('is-active');
        } else {
          qtyContainer.classList.remove('is-active');
        }
      }

      this.selectVariant(variantId);
    }

    /**
     * Prefetch and cache product data on hover
     */
    handleQuickAddHover(e) {
      const url = e.target.dataset.productUrl;
      if (!url) return;

      // Normalize URL
      let normalizedUrl = url;
      try {
        const urlObj = new URL(url, window.location.origin);
        normalizedUrl = urlObj.pathname;
      } catch (err) {
        // Already relative
      }

      // Check if already cached or being fetched
      const cached = productCache.get(normalizedUrl);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) return;
      if (pendingFetches.has(normalizedUrl)) return;

      // Start prefetch
      this.prefetchProduct(normalizedUrl);
    }

    /**
     * Prefetch product HTML and parse it for caching
     * Uses Section Rendering API for ~8x faster loading
     */
    async prefetchProduct(url) {
      const sectionUrl = this.getSectionRenderingUrl(url);
      const fetchPromise = fetch(sectionUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      });

      pendingFetches.set(url, fetchPromise);

      try {
        const response = await fetchPromise;
        if (response.ok) {
          const html = await response.text();
          const parsedData = this.parseProductHTML(html);
          if (parsedData) {
            productCache.set(url, {
              data: parsedData,
              timestamp: Date.now()
            });
          }
        }
      } catch (err) {
        // Silent fail for prefetch
      } finally {
        pendingFetches.delete(url);
      }
    }

    /**
     * Parse product HTML and extract all necessary data
     */
    parseProductHTML(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const doc = tmpl.content;

      const productEl = doc.querySelector('.cc-main-product .js-product') ||
                        doc.querySelector('.js-product') ||
                        doc.querySelector('[data-section]');

      if (!productEl) return null;

      // Extract basic product info
      // Get vendor only (not combined with SKU)
      const vendor = this.getElementText(productEl, '.product-vendor-sku .product-vendor') ||
                     this.getElementText(productEl, '.product-vendor');
      const title = this.getElementText(productEl, '.product-title');

      // Get first image
      const firstImg = productEl.querySelector('.product-gallery img, .media-viewer__item img');
      const imageSrc = firstImg ? (firstImg.src || firstImg.dataset.src) : '';

      // Extract variants - prioritize data-product-json (Section Rendering API primary source)
      let variants = [];
      let productData = null;
      let formattedInventory = {};

      // PRIMARY: data-product-json script (always included in quick-add-product section)
      const productJsonScript = doc.querySelector('script[data-product-json]');
      if (productJsonScript) {
        try {
          const parsed = JSON.parse(productJsonScript.textContent);
          if (parsed.product?.variants?.length) {
            variants = parsed.product.variants;
            productData = parsed.product;
            if (parsed.formatted) {
              formattedInventory = parsed.formatted;
            }
          }
        } catch (e) {
          console.warn('Quick Add: Failed to parse data-product-json', e);
        }
      }

      // Fallback: variant-list-cards component
      if (!variants.length) {
        const variantListCards = doc.querySelector('variant-list-cards');
        if (variantListCards) {
          const variantJson = variantListCards.querySelector('script[type="application/json"]');
          if (variantJson) {
            try {
              const parsed = JSON.parse(variantJson.textContent);
              variants = parsed.product?.variants || [];
              productData = parsed.product;
              if (parsed.formatted) {
                formattedInventory = parsed.formatted;
              }
            } catch (e) {}
          }
        }
      }

      // Fallback: variant-picker component
      if (!variants.length) {
        const variantPicker = doc.querySelector('variant-picker');
        if (variantPicker) {
          const variantJson = variantPicker.querySelector('script[type="application/json"]');
          if (variantJson) {
            try {
              const parsed = JSON.parse(variantJson.textContent);
              variants = parsed.product?.variants || [];
              productData = parsed.product;
              if (parsed.formatted) {
                formattedInventory = parsed.formatted;
              }
            } catch (e) {}
          }
        }
      }

      // Final fallback: search all JSONs
      if (!variants.length) {
        const allJsons = doc.querySelectorAll('script[type="application/json"]');
        for (const jsonEl of allJsons) {
          try {
            const parsed = JSON.parse(jsonEl.textContent);
            if (parsed.product?.variants?.length) {
              variants = parsed.product.variants;
              productData = parsed.product;
              if (parsed.formatted) {
                formattedInventory = parsed.formatted;
              }
              break;
            }
          } catch (e) {}
        }
      }

      // Apply formatted inventory and availability
      const hasFormattedInventory = Object.keys(formattedInventory).length > 0;
      if (hasFormattedInventory) {
        variants.forEach(v => {
          const formatted = formattedInventory[v.id];
          if (formatted) {
            if (typeof formatted.inventory === 'number') {
              v.inventory_quantity = formatted.inventory;
            }
            // Also apply availability status from Liquid (most accurate source)
            if (typeof formatted.available === 'boolean') {
              v.available = formatted.available;
            }
          }
        });
      }

      // Get variant images
      const variantImages = {};
      productEl.querySelectorAll('.media-viewer__item, [data-variant-ids]').forEach(item => {
        const img = item.querySelector('img');
        const variantIds = item.dataset.variantIds;
        if (img && variantIds) {
          variantIds.split(',').forEach(id => {
            variantImages[id.trim()] = img.src || img.dataset.src;
          });
        }
      });
      variants.forEach(v => {
        if (v.featured_image?.src) {
          variantImages[v.id] = v.featured_image.src;
        }
      });

      // Get inventory data from product-inventory element
      let inventoryData = [];
      const inventoryJson = doc.querySelector('product-inventory script[type="application/json"]');
      if (inventoryJson) {
        try {
          inventoryData = JSON.parse(inventoryJson.textContent);
        } catch (e) {}
      }

      // Get low stock threshold
      const inventoryEl = doc.querySelector('product-inventory');
      const lowStockThreshold = inventoryEl ? parseInt(inventoryEl.dataset.thresholdLow) || 10 : 10;

      return {
        vendor,
        title,
        imageSrc,
        variants,
        productData,
        variantImages,
        inventoryData,
        lowStockThreshold,
        hasFormattedInventory
      };
    }

    async open(opener) {
      this.isOpen = true;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Reset state
      this.variantQuantities = {};
      this.addedVariants.clear();
      this.addedQuantities = {};

      // Get product URL and normalize
      let productUrl = opener.dataset.productUrl;

      if (!productUrl) {
        this.showError();
        return;
      }

      try {
        const url = new URL(productUrl, window.location.origin);
        productUrl = url.pathname;
      } catch (e) {
        // Already relative
      }

      // Same product - just show (already rendered)
      if (this.productUrl === productUrl && this.content.querySelector('.qa-product-summary')) {
        return;
      }

      this.productUrl = productUrl;

      // Check cache first (instant render!)
      const cached = productCache.get(productUrl);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        this.renderFromCache(cached.data);
        return;
      }

      // Show loading
      this.showLoading();

      // If there's a pending fetch, wait for it to complete and check cache
      if (pendingFetches.has(productUrl)) {
        try {
          await pendingFetches.get(productUrl);
          // After pending fetch completes, check if it cached the data
          const cachedAfterWait = productCache.get(productUrl);
          if (cachedAfterWait && Date.now() - cachedAfterWait.timestamp < CACHE_TTL) {
            this.renderFromCache(cachedAfterWait.data);
            return;
          }
        } catch (e) {
          // Pending fetch failed, we'll try our own fetch
        }
      }

      // Start fresh fetch (don't reuse promises - response body can only be read once)
      // Uses Section Rendering API for ~8x faster loading
      try {
        const sectionUrl = this.getSectionRenderingUrl(productUrl);
        const response = await fetch(sectionUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });

        if (response.ok) {
          const html = await response.text();
          const parsedData = this.parseProductHTML(html);

          if (parsedData) {
            // Cache it
            productCache.set(productUrl, {
              data: parsedData,
              timestamp: Date.now()
            });

            this.renderFromCache(parsedData);
          } else {
            this.showError();
          }
        } else {
          this.showError();
        }
      } catch (error) {
        this.showError();
      }
    }

    /**
     * Render from cached data (instant!)
     */
    renderFromCache(data) {
      this.variants = data.variants;
      this.productData = data.productData;

      // Calculate totals
      let totalInventory = 0;
      let hasInventoryData = data.hasFormattedInventory;
      let availableCount = 0;

      // Apply inventory data
      this.variants.forEach(v => {
        const inv = data.inventoryData.find(i => i.id === v.id);
        if (inv) {
          v.inventory_quantity = inv.inventory_quantity;
          v.inventory_policy = inv.inventory_policy;
          hasInventoryData = true;
        }
        if (typeof v.inventory_quantity === 'number') {
          totalInventory += v.inventory_quantity;
          hasInventoryData = true;
        }
        if (v.available !== false) {
          availableCount++;
        }
      });

      // Get price range
      const prices = this.variants.map(v => {
        const price = v.price;
        return typeof price === 'string' ? parseFloat(price) || 0 : price || 0;
      }).filter(p => p > 0);

      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
      const comparePrice = this.variants[0]?.compare_at_price;

      // Build content
      this.content.innerHTML = `
        ${this.renderProductSummary(data.vendor, data.title, data.imageSrc, minPrice, maxPrice, comparePrice)}
        ${this.renderInventoryStatus(totalInventory, data.lowStockThreshold, hasInventoryData, availableCount, this.variants.length)}
        ${this.renderVariantsList(data.variantImages, data.inventoryData, data.lowStockThreshold)}
      `;

      // Render footer
      this.renderFooter();
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    showLoading() {
      // Skeleton loader that matches actual content layout for smoother transition
      this.content.innerHTML = `
        <div class="qa-skeleton">
          <div class="qa-skeleton__summary">
            <div class="qa-skeleton__image qa-skeleton__pulse"></div>
            <div class="qa-skeleton__info">
              <div class="qa-skeleton__line qa-skeleton__line--short qa-skeleton__pulse"></div>
              <div class="qa-skeleton__line qa-skeleton__line--long qa-skeleton__pulse"></div>
              <div class="qa-skeleton__line qa-skeleton__line--medium qa-skeleton__pulse"></div>
            </div>
          </div>
          <div class="qa-skeleton__inventory qa-skeleton__pulse"></div>
          <div class="qa-skeleton__header">
            <div class="qa-skeleton__line qa-skeleton__line--short qa-skeleton__pulse"></div>
            <div class="qa-skeleton__badge qa-skeleton__pulse"></div>
          </div>
          <div class="qa-skeleton__variants">
            <div class="qa-skeleton__variant qa-skeleton__pulse"></div>
            <div class="qa-skeleton__variant qa-skeleton__pulse"></div>
            <div class="qa-skeleton__variant qa-skeleton__pulse"></div>
          </div>
        </div>
      `;
      this.footer.innerHTML = '';
    }

    showError() {
      this.content.innerHTML = `
        <div class="qa-empty">
          <svg class="qa-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p class="qa-empty__text">無法載入商品資訊</p>
        </div>
      `;
    }

    renderProductSummary(vendor, title, imageSrc, minPrice, maxPrice, comparePrice) {
      const priceDisplay = minPrice === maxPrice
        ? this.formatMoney(minPrice)
        : `${this.formatMoney(minPrice)} - ${this.formatMoney(maxPrice)}`;

      // Store default image for reset
      this.defaultImageSrc = imageSrc;
      this.defaultPriceDisplay = priceDisplay;

      // Get first variant SKU as default
      const defaultSku = this.variants?.[0]?.sku || '';

      return `
        <div class="qa-product-summary">
          <div class="qa-product-summary__image" data-default-src="${imageSrc || ''}">
            ${imageSrc ? `<img src="${imageSrc.split('?')[0]}?width=160" alt="${title}">` : ''}
          </div>
          <div class="qa-product-summary__info">
            ${vendor ? `<div class="qa-product-summary__vendor">${vendor}</div>` : ''}
            <h3 class="qa-product-summary__title">
              <a href="${this.productUrl}">${title}</a>
            </h3>
            <div class="qa-product-summary__sku"${defaultSku ? '' : ' style="display:none"'}>${defaultSku ? `SKU: ${defaultSku}` : ''}</div>
            <div class="qa-product-summary__variant-name"></div>
            <div class="qa-product-summary__price">
              <span class="qa-product-summary__price-current">${priceDisplay}</span>
              ${comparePrice && comparePrice > maxPrice ? `<span class="qa-product-summary__price-was">${this.formatMoney(comparePrice)}</span>` : ''}
            </div>
          </div>
        </div>
      `;
    }

    selectVariant(variantId) {
      const variant = this.variants.find(v => v.id == variantId);
      if (!variant) return;

      this.selectedVariantId = variantId;

      // Update main image
      const imageContainer = this.content.querySelector('.qa-product-summary__image');
      if (imageContainer) {
        const variantImage = variant.featured_image?.src || variant.image?.src || this.defaultImageSrc;
        if (variantImage) {
          imageContainer.innerHTML = `<img src="${variantImage.split('?')[0]}?width=160" alt="${variant.title}">`;
        }
      }

      // Update variant name display (hide if it's "Default Title" for single-variant products)
      const variantNameEl = this.content.querySelector('.qa-product-summary__variant-name');
      if (variantNameEl) {
        const isDefaultTitle = !variant.title ||
                               variant.title === 'Default Title' ||
                               variant.title === 'Default' ||
                               variant.title === this.productData?.title;
        if (isDefaultTitle && this.variants.length === 1) {
          variantNameEl.style.display = 'none';
        } else {
          variantNameEl.textContent = variant.title;
          variantNameEl.style.display = 'block';
        }
      }

      // Update SKU display
      const skuEl = this.content.querySelector('.qa-product-summary__sku');
      if (skuEl) {
        if (variant.sku) {
          skuEl.textContent = `SKU: ${variant.sku}`;
          skuEl.style.display = 'block';
        } else {
          skuEl.style.display = 'none';
        }
      }

      // Update price display to show selected variant price
      const priceEl = this.content.querySelector('.qa-product-summary__price-current');
      if (priceEl) {
        priceEl.textContent = this.formatMoney(variant.price);
      }

      // Update compare-at-price if exists
      const comparePriceEl = this.content.querySelector('.qa-product-summary__price-was');
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = 'inline';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // Highlight selected variant card
      this.content.querySelectorAll('.qa-variant').forEach(card => {
        card.classList.remove('is-selected');
      });
      const selectedCard = this.content.querySelector(`.qa-variant[data-variant-id="${variantId}"]`);
      if (selectedCard) {
        selectedCard.classList.add('is-selected');
      }
    }

    renderInventoryStatus(totalInventory, lowStockThreshold, hasInventoryData = true, availableCount = 0, totalVariants = 0) {
      let statusClass = 'in-stock';
      let statusText = '有庫存';

      // If we don't have explicit inventory data, use availability from JSON API
      if (!hasInventoryData) {
        if (availableCount === 0) {
          statusClass = 'out-of-stock';
          statusText = '已售完';
        } else if (availableCount < totalVariants) {
          statusClass = 'low-stock';
          statusText = `${availableCount}/${totalVariants} 款有庫存`;
        } else {
          statusClass = 'in-stock';
          statusText = '有庫存';
        }
      } else if (totalInventory <= 0) {
        statusClass = 'out-of-stock';
        statusText = '已售完';
      } else if (totalInventory <= lowStockThreshold) {
        statusClass = 'low-stock';
        statusText = `僅剩 ${totalInventory} 件 - 即將售完`;
      } else {
        statusText = `庫存充足 (${totalInventory} 件)`;
      }

      // Calculate percentage for progress bar
      let percentage;
      if (!hasInventoryData) {
        percentage = (availableCount / totalVariants) * 100;
      } else {
        percentage = Math.min((totalInventory / (lowStockThreshold * 3)) * 100, 100);
      }

      return `
        <div class="qa-inventory qa-inventory--${statusClass}">
          <div class="qa-inventory__status">
            <span class="qa-inventory__dot"></span>
            <span class="qa-inventory__text">${statusText}</span>
          </div>
          ${totalInventory > 0 && totalInventory <= lowStockThreshold * 2 ? `
            <div class="qa-inventory__bar">
              <div class="qa-inventory__bar-fill" style="width: ${percentage}%"></div>
            </div>
          ` : ''}
        </div>
      `;
    }

    renderVariantsList(variantImages, _inventoryData, lowStockThreshold) {
      if (!this.variants.length) {
        return `
          <div class="qa-empty">
            <p class="qa-empty__text">無可用規格</p>
          </div>
        `;
      }

      const variantCards = this.variants.map((variant, index) => {
        // For single-variant "Default Title" products, use product title instead
        const isSingleDefault = this.variants.length === 1 &&
          (variant.title === 'Default Title' || !variant.title || variant.title === 'Default');

        if (isSingleDefault) {
          // Auto-select the only variant
          this.selectedVariantId = variant.id;
        }
        // Get variant image - try multiple sources
        let imageSrc = variantImages[variant.id] ||
                       variant.featured_image?.src ||
                       variant.image?.src ||
                       this.defaultImageSrc ||
                       '';

        // Check if we have explicit inventory data (from HTML page) or just availability (from JSON API)
        const hasInventoryData = typeof variant.inventory_quantity === 'number' && variant.inventory_quantity !== null;
        const inventory = hasInventoryData ? variant.inventory_quantity : null;
        const available = variant.available !== false;
        const canBackorder = variant.inventory_policy === 'continue';

        // Get variant title with fallback - use product title for single "Default Title" variants
        let variantTitle = variant.title || variant.name || `選項 ${index + 1}`;
        if (isSingleDefault) {
          variantTitle = this.productJsonData?.title || this.productData?.title || '單一規格';
        }
        const variantPrice = variant.price || 0;
        const variantSku = variant.sku || '';

        let stockClass = 'in-stock';
        let stockText = '有庫存';

        // Prioritize the `available` boolean from API over inventory numbers
        if (!available) {
          // Variant is marked as unavailable
          stockClass = 'out-of-stock';
          stockText = '無庫存';
        } else if (hasInventoryData && inventory <= 0 && canBackorder) {
          // Has inventory data, out of stock but can backorder
          stockClass = 'low-stock';
          stockText = '可預購';
        } else if (hasInventoryData && inventory <= 0) {
          // Has inventory data, out of stock
          stockClass = 'out-of-stock';
          stockText = '無庫存';
        } else if (hasInventoryData && inventory > 0 && inventory <= lowStockThreshold) {
          // Has inventory data, low stock
          stockClass = 'low-stock';
          stockText = `剩 ${inventory} 件`;
        } else if (hasInventoryData && inventory > lowStockThreshold) {
          // Has inventory data, in stock
          stockClass = 'in-stock';
          stockText = `${inventory} 件`;
        } else if (available) {
          // No inventory data but marked as available - trust the API
          stockClass = 'in-stock';
          stockText = '有庫存';
        }

        // Only grey out if explicitly unavailable
        const isUnavailable = !available;
        this.variantQuantities[variant.id] = 0;

        return `
          <div class="qa-variant ${isUnavailable ? 'is-unavailable' : ''}" data-variant-id="${variant.id}">
            <div class="qa-variant__image">
              ${imageSrc
                ? `<img src="${imageSrc.replace(/\?.*$/, '')}?width=120" alt="${variantTitle}" loading="lazy">`
                : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>`
              }
            </div>
            <div class="qa-variant__info">
              <div class="qa-variant__name">${variantTitle}</div>
              ${variantSku ? `<div class="qa-variant__sku">SKU: ${variantSku}</div>` : ''}
              <div class="qa-variant__meta">
                <span class="qa-variant__price">${this.formatMoney(variantPrice)}</span>
                <span class="qa-variant__stock qa-variant__stock--${stockClass}">${stockText}</span>
              </div>
            </div>
            ${!isUnavailable ? `
              <div class="qa-variant__controls">
                <div class="qa-variant__qty" data-variant-id="${variant.id}">
                  <button type="button" class="qa-variant__qty-btn" data-action="decrease" data-variant-id="${variant.id}" disabled>−</button>
                  <input type="number" class="qa-variant__qty-input" data-variant-id="${variant.id}" value="0" min="0" max="999" inputmode="numeric" pattern="[0-9]*">
                  <button type="button" class="qa-variant__qty-btn" data-action="increase" data-variant-id="${variant.id}">+</button>
                </div>
                <button type="button" class="qa-variant__add" data-variant-id="${variant.id}" title="加入購物車">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </button>
              </div>
            ` : ''}
          </div>
        `;
      }).join('');

      return `
        <div class="qa-variants">
          <div class="qa-variants__header">
            <span class="qa-variants__title">選擇規格</span>
            <span class="qa-variants__count">${this.variants.length} 種規格</span>
          </div>
          <div class="qa-variants__list">
            ${variantCards}
          </div>
        </div>
      `;
    }

    renderSingleVariantProduct(variant, lowStockThreshold) {
      // For products with no variant options (single Default Title variant)
      const hasInventoryData = typeof variant.inventory_quantity === 'number' && variant.inventory_quantity !== null;
      const inventory = hasInventoryData ? variant.inventory_quantity : null;
      const available = variant.available !== false;
      const canBackorder = variant.inventory_policy === 'continue';

      let stockClass = 'in-stock';
      let stockText = '有庫存';

      if (!available) {
        stockClass = 'out-of-stock';
        stockText = '無庫存';
      } else if (hasInventoryData && inventory <= 0 && canBackorder) {
        stockClass = 'low-stock';
        stockText = '可預購';
      } else if (hasInventoryData && inventory <= 0) {
        stockClass = 'out-of-stock';
        stockText = '無庫存';
      } else if (hasInventoryData && inventory > 0 && inventory <= lowStockThreshold) {
        stockClass = 'low-stock';
        stockText = `剩 ${inventory} 件`;
      } else if (hasInventoryData && inventory > lowStockThreshold) {
        stockClass = 'in-stock';
        stockText = `庫存 ${inventory} 件`;
      } else if (available) {
        stockClass = 'in-stock';
        stockText = '有庫存';
      }

      const isUnavailable = !available;
      this.variantQuantities[variant.id] = 0;
      this.selectedVariantId = variant.id; // Auto-select the only variant

      return `
        <div class="qa-single-variant">
          <div class="qa-single-variant__stock qa-single-variant__stock--${stockClass}">
            <span class="qa-single-variant__dot"></span>
            <span>${stockText}</span>
          </div>
          <div class="qa-single-variant__actions">
            <div class="qa-single-variant__qty qa-variant__qty" data-variant-id="${variant.id}">
              <button type="button" class="qa-variant__qty-btn" data-action="decrease" data-variant-id="${variant.id}" disabled>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <input type="number" class="qa-variant__qty-input" data-variant-id="${variant.id}" value="0" min="0" max="999" inputmode="numeric" pattern="[0-9]*">
              <button type="button" class="qa-variant__qty-btn" data-action="increase" data-variant-id="${variant.id}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
            </div>
            <button type="button" class="qa-single-variant__add qa-variant__add ${isUnavailable ? 'is-disabled' : ''}"
                    data-variant-id="${variant.id}" ${isUnavailable ? 'disabled' : ''}>
              ${isUnavailable ? '已售完' : '加入購物車'}
            </button>
          </div>
        </div>
      `;
    }

    // Event delegation handles all quantity controls - no need for initQuantityControls

    async addVariantToCart(btn) {
      const variantId = btn.dataset.variantId;
      let qty = this.variantQuantities[variantId] || 0;

      // If qty is 0, auto-set to 1 and update UI
      if (qty <= 0) {
        qty = 1;
        this.variantQuantities[variantId] = qty;
        const inputEl = this.content.querySelector(`.qa-variant__qty-input[data-variant-id="${variantId}"]`);
        const qtyContainer = this.content.querySelector(`.qa-variant__qty[data-variant-id="${variantId}"]`);
        if (inputEl) inputEl.value = qty;
        if (qtyContainer) qtyContainer.classList.add('is-active');
      }

      btn.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', qty);

        // Always request cart-icon-bubble section for header badge update
        let sectionsToRequest = ['cart-icon-bubble'];

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const cartSection = cartDrawer.closest('.shopify-section');
          if (cartSection) {
            const sectionId = cartSection.id.replace('shopify-section-', '');
            sectionsToRequest.push(sectionId);
          }
        }

        formData.append('sections', sectionsToRequest.join(','));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Failed to add to cart');
        }

        // Update the drawer, icon bubble, and dispatch a consistent
        // completion event (on:cart:change + legacy cart:updated) — see
        // window.cartDrawerCoordinator.applyCartUpdate in cart-drawer-coordinator.js.
        if (window.cartDrawerCoordinator) {
          window.cartDrawerCoordinator.applyCartUpdate(data, { openDrawer: true });
        }

        // Show success state on button
        this.showAddedState(btn);
        this.addedVariants.add(variantId);

        // Track total quantity added for this variant
        if (!this.addedQuantities[variantId]) {
          this.addedQuantities[variantId] = 0;
        }
        this.addedQuantities[variantId] += qty;

        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: {
            title: data.product_title || data.title,
            variantTitle: data.variant_title,
            image: data.image || (data.featured_image && data.featured_image.url),
            price: data.final_price,
            quantity: qty,
            url: data.url
          }
        }));

        // Update footer with total items added
        this.updateFooter();

      } catch (error) {
        console.error('Error adding to cart:', error);
        document.dispatchEvent(new CustomEvent('cart:add-error', {
          detail: { message: error.message || '無法加入購物車' }
        }));
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    showAddedState(btn) {
      const controls = btn.closest('.qa-variant__controls');
      btn.outerHTML = `
        <div class="qa-variant__added">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
      `;

      // Revert after 2 seconds
      setTimeout(() => {
        const addedEl = controls.querySelector('.qa-variant__added');
        if (addedEl) {
          const variantId = controls.querySelector('.qa-variant__qty-btn')?.dataset.variantId;
          addedEl.outerHTML = `
            <button type="button" class="qa-variant__add" data-variant-id="${variantId}" title="加入購物車">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
          `;
          // Rebind event
          const newBtn = controls.querySelector('.qa-variant__add');
          if (newBtn) {
            newBtn.addEventListener('click', () => this.addVariantToCart(newBtn));
          }
        }
      }, 2000);
    }

    renderFooter() {
      this.footer.innerHTML = `
        <div class="qa-footer__summary">
          <span class="qa-footer__items-count">已加入 <strong>0</strong> 件商品</span>
          <a href="${this.productUrl}" class="qa-footer__link" style="font-size: 13px; color: var(--qa-accent);">查看商品詳情 →</a>
        </div>
        <div class="qa-footer__buttons">
          <button type="button" class="qa-footer__btn qa-footer__btn--secondary js-continue">
            繼續購物
          </button>
          <button type="button" class="qa-footer__btn js-view-cart">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            查看購物車
          </button>
        </div>
      `;

      // Bind footer events
      this.footer.querySelector('.js-view-cart')?.addEventListener('click', () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          this.close();
          setTimeout(() => cartDrawer.open(), 300);
        } else {
          window.location.href = '/cart';
        }
      });

      this.footer.querySelector('.js-continue')?.addEventListener('click', () => {
        this.close();
      });
    }

    updateFooter() {
      const countEl = this.footer.querySelector('.qa-footer__items-count strong');
      if (countEl) {
        // Sum all quantities added across all variants
        const totalAdded = Object.values(this.addedQuantities).reduce((sum, qty) => sum + qty, 0);
        countEl.textContent = totalAdded;
      }
    }

    formatMoney(value) {
      if (value === null || value === undefined) return '$0';

      let numValue;

      // Handle string prices from JSON API (like "1320.00" for $1,320)
      // JSON API returns prices as STRINGS already in dollars - DON'T divide
      if (typeof value === 'string') {
        numValue = parseFloat(value);
        if (isNaN(numValue)) return '$0';
        // String values from JSON API are already in dollars
        return '$' + Math.round(numValue).toLocaleString();
      }

      // Handle numeric prices from Liquid's product | json
      // Shopify stores ALL prices in smallest currency unit (cents)
      // Even for TWD which has no decimal places, prices are multiplied by 100
      // So $1,320 TWD is stored as 132000, needs to be divided by 100
      if (typeof value === 'number') {
        const dollars = value / 100;
        return '$' + Math.round(dollars).toLocaleString();
      }

      return '$0';
    }

    getElementText(el, selector) {
      const elem = el.querySelector(selector);
      return elem ? elem.textContent.trim() : '';
    }
  }

  customElements.define('quick-add-drawer-fun', QuickAddDrawerFun);
}
